import React, { useEffect } from 'react';
import styled from '@emotion/styled';
import { useDeviceSize } from '@vmos2/providers';

import { IconButton } from '../buttons';
import { Title as DSTitle } from '../titles/Title';
import { useComponentTheme } from '../useComponentTheme';
import { useTranslation } from 'react-i18next';

type CloseButtonProps = {
  className?: string,
  ariaLabel?: string,
  onClick: Function,
};

export const CloseButton = ({ className, ariaLabel, onClick }: CloseButtonProps) => {
  const { isLarge } = useDeviceSize();
  const { t } = useTranslation('common');

  return (
    <CloseBtn
      className={className}
      onClick={onClick}
      mode="rounded"
      icon="Times"
      aria-label={ariaLabel || t('close')}
      data-test="customization-page-back-button"
      size={isLarge ? 'small' : 'xsmall'}
    />
  );
};

type CloseButtonHeaderProps = {
  className: ?string,
  title?: string,
  titleAriaHidden?: boolean,
  titleId?: string,
  isBold: boolean,
  handleCloseClick?: Function,
  iconLeft: ?React.Node,
  isSticky?: boolean,
  hasShadow?: boolean,
  contentScrolled: boolean,
  isTransparent: boolean,
  titleCentered?: boolean,
  contentRight?: React.Node,
  hideCloseButton?: boolean,
  closeButtonAriaLabel?: string,
  titleAriaLabel?: string,
  isLinearHeader?: boolean,
  mode?: string,
  focusTitleOnMount?: boolean,
  titleFocusTimeout?: number,
};

export const CloseButtonHeader = ({
  className,
  title,
  titleAriaHidden,
  titleId,
  handleCloseClick,
  iconLeft,
  isSticky,
  hasShadow,
  contentScrolled,
  isBold,
  isTransparent,
  titleCentered,
  contentRight,
  hideCloseButton,
  isLinearHeader,
  closeButtonAriaLabel,
  titleAriaLabel,
  mode,
  focusTitleOnMount = false,
  titleFocusTimeout = 0,
  ...rest
}: CloseButtonHeaderProps) => {
  const { isTablet } = useDeviceSize();

  useEffect(() => {
    const handleEsc = event => {
      if (event.keyCode === 27 && handleCloseClick) {
        handleCloseClick();
      }
    };

    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [handleCloseClick]);

  const componentTheme = useComponentTheme(theme => theme.components.Modal, mode);

  return (
    <Header
      {...rest}
      className={className}
      isSticky={isSticky}
      contentScrolled={contentScrolled}
      hasShadow={hasShadow}
      isTransparent={isTransparent}
      isLinearHeader={isLinearHeader}
      componentTheme={componentTheme?.Header}
    >
      {iconLeft}

      {title && (
        <Title
          align={titleCentered ? 'center' : 'left'}
          ariaHidden={titleAriaHidden}
          id={titleId}
          isSticky={isSticky}
          level={1}
          margin="none"
          ml={!isTablet && iconLeft ? 10 : 0}
          mode={mode}
          titleCentered
          ariaLabel={titleAriaLabel}
          autoFocus={focusTitleOnMount}
          autoFocusTimeout={titleFocusTimeout}
        >
          {title}
        </Title>
      )}

      {contentRight}

      {!hideCloseButton && handleCloseClick && (
        <CloseButton ariaLabel={closeButtonAriaLabel} onClick={handleCloseClick} />
      )}
    </Header>
  );
};

const Header = styled.header`
  display: flex;
  width: 100%;
  min-height: ${({ theme }) => theme.constants.headerHeight};
  flex-basis: ${({ theme }) => theme.constants.headerHeight};
  align-items: center;
  justify-content: space-between;
  padding: 12px 12px 12px 24px;
  z-index: ${({ theme, isLinearHeader }) => (isLinearHeader ? theme.positioning.statusBar : theme.positioning.header)};
  background: ${({ theme, isTransparent }) => !isTransparent && theme.color.white};

  ${({ isSticky }) =>
    isSticky &&
    `
      position: fixed;
      width: 100%;
      top: 0;
      left: 0;
      padding-top: calc(env(safe-area-inset-top) + 12px);
    `};

  ${({ hasShadow, isSticky, isTransparent, theme, isLinearHeader, componentTheme }) =>
    (hasShadow || (isSticky && !isTransparent)) &&
    !isLinearHeader &&
    `
      box-shadow: ${componentTheme?.boxShadow || theme.boxShadow.medium};
    `};

  @media (${({ theme }) => theme.breakpoint.desktop}) {
    padding: 18px 18px 18px 32px;

    ${({ theme, contentScrolled }) =>
      contentScrolled &&
      `
      box-shadow: ${theme.boxShadow.medium};
    `}
  }
`;

const Title = styled(DSTitle)`
  outline: none;

  ${({ isSticky }) =>
    isSticky &&
    `
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    padding-right: 28px;
  `}

  ${({ titleCentered }) => titleCentered && `width: 100%;`}
`;

const CloseBtn = styled(IconButton)`
  margin-left: auto;
  position: relative;
  visibility: visible;
  background: ${({ theme }) => theme.color.white};
  z-index: ${({ theme }) => theme.positioning.header};
  font-size: ${({ theme }) => theme.fontSize.xl};

  @media (${({ theme }) => theme.breakpoint.kiosk}) {
    position: absolute;
    top: 6px;
    right: 6px;
    width: 64px;
    height: 64px;
  }
`;

CloseButtonHeader.defaultProps = {
  leftIcon: null,
  className: null,
  contentScrolled: false,
  isBold: false,
  iconLeft: null,
  isTransparent: false,
};
